import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  InputLabel,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { AppStatus } from 'utils/dropDownList';
import {
  getAppointment,
  getProviderOfficeExamRoom,
  saveAppointment,
} from 'store/slices/patientInfoSlice';
import { useAppDispatch } from 'store/hooks';
import { EditAppointmentPayload } from './EditAppointment';
import SearchBox from 'utils/searchBox';
import PatientSearchBox, { PatientOption } from './PatientSearchBox';
import { profile } from 'console';
import { toast } from 'react-toastify';
import { StyledDialog } from 'theme/index';
import { SCHEDULING_UNAVAILABLE_MESSAGE } from 'shared/content/constants';

export type NewAppointmentProps = {
  dateTime: string;
  duration: number;
  officeId: number;
  profileId: number;
  profileName: string;
  providerId: number;
  examRoom: number;
  offices: any[];
  providers: any[];

  loading?: boolean;
  setLoading?: (val: boolean) => void;
  onClose?: () => void;
  onSaveAppointment?: (data: any) => void;
};
type FormDataType = {
  date: Dayjs | null;
  time: Dayjs | null;
  doctor: number;
  office: number;
  profileName: string;
  providerId: number;
  examRoom: number;
  duration: number;
  status: string;
  reason: string;
  patientName: string;
  patientId?: number;
};
const NewAppointment = (props: NewAppointmentProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const duration = props.duration || 0;
  const profileId = props.profileId || 0;
  const officeId = props.officeId || 0;
  const providerId = props.providerId || 0;
  const profileName = props.profileName || '';
  const officeList = props.offices || [];
  const providerList = props.providers || [];
  const dateTime = dayjs.utc(props.dateTime);
  const [examRoom] = useState(props.examRoom || 0);
  const [examRoomList, setExamRoomList] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<FormDataType>({
    doctor: providerId,
    office: officeId,
    profileName,
    providerId,
    examRoom,
    date: dateTime,
    time: dateTime,
    duration,
    status: '',
    reason: '',
    patientName: '',
  });
  useEffect(() => {
    dispatch(getProviderOfficeExamRoom({ officeId }))
      .unwrap()
      .then((res: any) => {
        if (res?.status === 200) {
          setExamRoomList(res?.data || []);
        }
      });
    setAppointmentsLoading(true);
    props.setLoading?.(false);
  }, [officeId, dispatch]);

  // ✅ Populate data when editing

  // ✅ Handle change
  const handleChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === 'doctor' || field === 'office' || field === 'profileName' || field === 'duration'
          ? Number(event.target.value)
          : event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleDateChange = (value: Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      date: value,
    }));
  };

  const handleTimeChange = (value: Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      time: value,
    }));
  };

  // ✅ Close dialog
  const onClose = () => {
    props.onClose?.();
  };

  // ✅ Save handler
  const handleSave = () => {
    // ✅ VALIDATE FIRST
    if (!validateForm()) return;

    let scheduled_time = null;

    if (formData.date && formData.time) {
      scheduled_time = dayjs(formData.date)
        .hour(formData.time.hour())
        .minute(formData.time.minute())
        .second(0)
        .format('YYYY-MM-DDTHH:mm:ss');
    }

    const payload = {
      ...formData,
      scheduledTime: scheduled_time,
      profileId,
    };

    createAppointment(payload);
  };

  const createAppointment = (payload: any) => {
    dispatch(saveAppointment(payload))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const data = response?.data;
          if (data.apiResult) {
            toast.success('Appointment created successfully');
            props.onSaveAppointment?.(payload);
            return;
          } else if (data.throttled) {
            toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
          } else if (data.messsage.includes('out of range')) {
            toast.error(
              'Examination room number is out of range for the selected office, Please revsit appointent configuration'
            );
          } else if (data.messsage.includes('overlaps')) {
            toast.error('Appointment overlaps with an existing appointment');
          } else if (data.messsage.includes('inactive patient')) {
            toast.error('Selected patient is inactive');
          } else {
            toast.error(data.messsage || 'Failed to create appointment');
          }
        } else {
          toast.error('Failed to create appointment');
        }
      });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.doctor) newErrors.doctor = 'Provider is required';
    if (!formData.profileName) newErrors.profileName = 'Appointment Template is required';
    if (!formData.examRoom) newErrors.examRoom = 'Exam room is required';
    if (!formData.office) newErrors.office = 'Office is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.patientId) {
      newErrors.patientName = 'Patient is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <StyledDialog open={true} onClose={onClose} maxWidth='md' fullWidth>
      {/* 🔥 Loader Overlay */}
      <Backdrop
        open={props.loading || false}
        sx={{
          position: 'absolute',
          zIndex: 1300,
          color: '#fff',
        }}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <DialogTitle>Create Appointment</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Provider */}
          <Grid item xs={4}>
            <InputLabel required>Provider</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.doctor}
              onChange={handleChange('doctor')}
              error={!!errors.doctor}
              helperText={errors.doctor}
              disabled={true}
            >
              {providerList.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Patient Name */}
          <Grid item xs={4}>
            <InputLabel required>Patient</InputLabel>
            <PatientSearchBox
              onSelect={(patient) => {
                setFormData((prev) => ({
                  ...prev,
                  patientName: patient.label,
                  patientId: patient.id,
                }));

                setErrors((prev) => ({
                  ...prev,
                  patientName: '',
                }));
              }}
              onTyping={() => {
                setFormData((prev) => {
                  const updated = {
                    ...prev,
                    patientName: '',
                    patientId: undefined,
                  };
                  return updated;
                });
              }}
              error={!!errors.patientName}
              helperText={errors.patientName}
            />
          </Grid>
          {/* Profile */}
          <Grid item xs={4}>
            <InputLabel required>Appointment Template</InputLabel>
            <TextField fullWidth size='small' value={formData.profileName} disabled />
          </Grid>
          {/* Profile */}
          <Grid item xs={4}>
            <InputLabel required>Exam Room</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.examRoom}
              onChange={handleChange('examRoom')}
              disabled={examRoomList.length !== 0}
              error={!!errors.examRoom}
              helperText={errors.examRoom}
            >
              {examRoomList.map((p: any) => (
                <MenuItem key={p.index} value={p.index}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Office */}
          <Grid item xs={4}>
            <InputLabel required>Office</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.office}
              onChange={handleChange('office')}
              error={!!errors.office}
              helperText={errors.office}
              disabled={officeList.length > 0}
            >
              {officeList.map((o: any) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Patient Name */}
          <Grid item xs={4}>
            <InputLabel>Reason</InputLabel>
            <TextField
              fullWidth
              size='small'
              value={formData.reason}
              onChange={handleChange('reason')}
            />
          </Grid>
          {/* Date */}
          <Grid item xs={3}>
            <InputLabel required>Schedule</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formData.date}
                onChange={handleDateChange}
                disabled={true} // Disable if time is selected to prevent confusion
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: !!errors.date,
                    helperText: errors.date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Time */}
          <Grid item xs={2}>
            <InputLabel required>Time </InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                disabled={true}
                value={formData.time}
                onChange={handleTimeChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    error: !!errors.time,
                    helperText: errors.time,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Duration */}
          <Grid item xs={2}>
            <InputLabel>Duration(minutes)</InputLabel>
            <TextField
              type='number'
              fullWidth
              size='small'
              value={formData.duration}
              onChange={handleChange('duration')}
              disabled={true}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button variant='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave}>
          OK
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default NewAppointment;
